import { FC } from 'react';
import { Button, Text, Box } from '@doda/io-react/common';
import { CONDITION_CHANGE_LINK } from 'components/SearchSideBar/CurrentCondition/constants';
import { DetailButton } from 'components/SearchSideBar/CurrentCondition/DetailButton';
import { useCurrentCondition } from 'components/SearchSideBar/CurrentCondition/hooks/useCurrentCondition';
import { ModalOverlay } from 'components/ModalOverlay';
import { SearchConditionLabelResponse } from '@doda/api-client/aspida/@types';
import { saveCurrentCondition } from 'logics/saveCurrentCondition';
import { createSearchConditionName } from '@doda/common/src/logics/saveCondition/saveCondition';
import { LabelText } from 'components/SearchSideBar/CurrentCondition/labelText';
import { SelectedCurrentConditionType } from 'types/searchCondition';
import dynamic from 'next/dynamic';
import { createSaveConditionsRequest } from 'logics/createSaveConditionsRequest';
import { EXTEND_CONDITION_QUERY } from 'components/SearchSideBar/constants';
import style from './index.module.scss';

const OverwriteConditionModal = dynamic(
  () =>
    import('components/OrverwriteConditionModal').then((module) => ({
      default: module.OverwriteConditionModal,
    })),
  { ssr: false }
);
const CurrentConditionModal = dynamic(
  () =>
    import('components/CurrentConditionModal').then((module) => ({
      default: module.CurrentConditionModal,
    })),
  { ssr: false }
);

/**
 * Propsの型定義
 */
interface Props {
  isLogin: boolean;
  searchConditionLabel: string | undefined;
  savedSearchCount: number;
  selectedCurrentCondition: SelectedCurrentConditionType;
  currentConditionData: SearchConditionLabelResponse;
}

export const CurrentCondition: FC<Props> = ({
  isLogin,
  searchConditionLabel,
  savedSearchCount,
  selectedCurrentCondition,
  currentConditionData,
}) => {
  const {
    detailModal,
    overWriteModal,
    onClickSaveButtonMatchJobList,
    isShowSaveButton,
  } = useCurrentCondition({
    isLogin,
    searchConditionLabel,
    savedSearchCount,
    selectedCurrentCondition,
  });

  const conditionChangeLink = isLogin
    ? CONDITION_CHANGE_LINK.LOGIN_STATE_LINK
    : CONDITION_CHANGE_LINK.LOGOUT_STATE_LINK;

  return (
    <>
      <Box className={style['current-condition__wrapper']} radius="4">
        <div className={style['current-condition__heading']}>
          <Text size="16" weight="bold">
            現在の検索条件
          </Text>
          <span>
            [
            <a
              href={`${conditionChangeLink}?${EXTEND_CONDITION_QUERY.MATCH_JOB_LIST}`}
              className={style['current-condition__link']}
            >
              条件変更
            </a>
            ]
          </span>
        </div>
        <LabelText searchConditionLabel={searchConditionLabel} />
        {isShowSaveButton && <DetailButton onClick={detailModal.onOpen} />}
        {isShowSaveButton && (
          <Button
            variant="outline_gray"
            widthPercentage={100}
            onClick={onClickSaveButtonMatchJobList}
          >
            検索条件を保存
          </Button>
        )}
      </Box>
      <ModalOverlay
        show={overWriteModal.isOpen}
        onClickOverlay={overWriteModal.onClose}
      >
        {overWriteModal.isOpen && (
          <OverwriteConditionModal
            onClickClose={overWriteModal.onClose}
            conditions={createSaveConditionsRequest(selectedCurrentCondition)}
          />
        )}
      </ModalOverlay>
      <ModalOverlay
        show={detailModal.isOpen}
        onClickOverlay={detailModal.onClose}
      >
        {detailModal.isOpen && (
          <CurrentConditionModal
            defaultConditionName={createSearchConditionName()}
            currentConditionData={currentConditionData}
            onClickClose={detailModal.onClose}
            onClickSave={(conditionName) => {
              saveCurrentCondition({
                conditions: createSaveConditionsRequest(
                  selectedCurrentCondition
                ),
                isLogin,
                conditionName,
                savedSearchCount,
              });
            }}
          />
        )}
      </ModalOverlay>
    </>
  );
};
